import React, { useEffect, useMemo } from "react";
import clsx from "clsx";
import { makeStyles, TextField, Button, Chip } from "@material-ui/core";
import { Formik, Form, Field, useFormikContext } from "formik";
import { FileCopyOutlined } from "@material-ui/icons";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { i18n } from "../../translate/i18n";
import { getTokens } from "../../styles/tokens";

const useStyles = makeStyles(theme => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    root: { width: "100%", minWidth: 0 },
    toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 12 },
    hint: { flex: "1 1 220px", margin: 0, fontFamily: t.font, fontSize: 12.5, lineHeight: 1.5, color: t.textSecondary },
    week: { border: `1px solid ${t.line}`, borderRadius: t.radius.md, overflow: "hidden", backgroundColor: t.paper },
    // A linha se arruma pela largura do CONTÊINER (modal sm/md, página, celular), não da janela:
    // os períodos ficam lado a lado, empilhados à direita do dia ou abaixo dele, conforme couber.
    row: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 16px", padding: "10px 14px", borderBottom: `1px solid ${t.lineRow}`, "&:last-child": { borderBottom: 0 } },
    day: { flex: "0 0 124px", fontFamily: t.font, fontSize: 13, fontWeight: 600, lineHeight: 1.4, color: t.text },
    periods: { flex: "1 1 330px", minWidth: 0, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(330px, 100%), 1fr))", gap: "8px 20px" },
    // no celular o rótulo do período sobe para a linha de cima: o campo de hora em 12 h ("08:00 AM") precisa de ~120 px
    period: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 8px", minWidth: 0 },
    periodLabel: { flex: "0 0 64px", fontFamily: t.font, fontSize: 12, color: t.textSecondary, whiteSpace: "nowrap" },
    range: { flex: "1 1 250px", minWidth: 0, display: "flex", alignItems: "center", gap: 8 },
    time: { flex: "1 1 0", minWidth: 0, maxWidth: 150, margin: 0 },
    until: { flex: "none", fontFamily: t.font, fontSize: 12, color: t.textMuted },
    footer: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginTop: 16 },
    // rodapé hospedado (ex.: DialogActions): o selo de estado vai para a esquerda
    footerStart: { marginRight: "auto" },
  };
});
const weekdays = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const fields = ["startTimeA", "endTimeA", "startTimeB", "endTimeB"];
const periods = [{ label: "1º período", fields: ["startTimeA", "endTimeA"] }, { label: "2º período", fields: ["startTimeB", "endTimeB"] }];
const emptyWeek = () => weekdays.map(day => ({ weekday: i18n.t(`queueModal.serviceHours.${day}`), weekdayEn: day, startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "" }));

function SyncScheduleDraft({ onChange }) {
  const { values } = useFormikContext();
  useEffect(() => { if (onChange) onChange(values.schedules); }, [values.schedules, onChange]);
  return null;
}
/**
 * Grade de horários da semana (dois períodos por dia).
 * `className`, `BodyComponent` e `FooterComponent` são opcionais e só servem para o anfitrião encaixar
 * o corpo e o rodapé no próprio layout (ex.: DialogContent/DialogActions de um modal).
 */
export default function SchedulesForm({ initialValues, onSubmit, loading, labelSaveButton, embedded = false, onChange, className, BodyComponent, FooterComponent }) {
  const c = useStyles();
  const schedules = useMemo(() => Array.isArray(initialValues) && initialValues.length ? initialValues : emptyWeek(), [initialValues]);
  const Container = embedded ? "div" : Form;
  const Body = BodyComponent || React.Fragment;
  const Footer = FooterComponent || "div";
  return <Formik enableReinitialize initialValues={{ schedules }} onSubmit={values => onSubmit(values.schedules)}>
    {({ values, setFieldValue, dirty }) => <Container className={clsx(c.root, className)}>
      {embedded && <SyncScheduleDraft onChange={onChange} />}
      <Body>
        <div className={c.toolbar}>
          <p className={c.hint}>Configure até dois períodos por dia. As alterações são aplicadas ao salvar.</p>
          <Button type="button" size="small" variant="outlined" startIcon={<FileCopyOutlined />} disabled={loading || !values.schedules.some(day => day.weekdayEn === "monday")} onClick={() => {
            const monday = values.schedules.find(day => day.weekdayEn === "monday");
            if (!monday) return;
            const hours = Object.fromEntries(fields.map(field => [field, monday[field] || ""]));
            setFieldValue("schedules", values.schedules.map(day => ["tuesday", "wednesday", "thursday", "friday"].includes(day.weekdayEn) ? { ...day, ...hours } : day));
          }}>Repetir segunda nos dias úteis</Button>
        </div>
        <div className={c.week}>
          {values.schedules.map((day, index) => <div key={day.weekdayEn || index} className={c.row}>
            <div className={c.day}>{day.weekdayEn ? i18n.t(`queueModal.serviceHours.${day.weekdayEn}`) : day.weekday}</div>
            <div className={c.periods}>
              {periods.map(period => <div key={period.label} className={c.period}>
                <span className={c.periodLabel}>{period.label}</span>
                <div className={c.range}>
                  {period.fields.map((field, fieldIndex) => <React.Fragment key={field}>
                    {fieldIndex === 1 && <span className={c.until}>até</span>}
                    <Field name={`schedules[${index}].${field}`}>
                      {({ field: input }) => <TextField {...input} value={input.value || ""} className={c.time} size="small" variant="outlined" type="time" inputProps={{ "aria-label": `${day.weekday} — ${i18n.t(`queueModal.serviceHours.${field}`)}`, step: 60 }} disabled={loading} />}
                    </Field>
                  </React.Fragment>)}
                </div>
              </div>)}
            </div>
          </div>)}
        </div>
      </Body>
      {!embedded && <Footer className={FooterComponent ? undefined : c.footer}>
        <Chip size="small" variant="outlined" className={FooterComponent ? c.footerStart : undefined} label={dirty ? "Alterações ainda não salvas" : "Horários carregados"} color={dirty ? "primary" : "default"} />
        <ButtonWithSpinner loading={loading} type="submit" color="primary" variant="contained">{labelSaveButton || "Salvar horários"}</ButtonWithSpinner>
      </Footer>}
    </Container>}
  </Formik>;
}
